import { ApplicationConfig, inject, LOCALE_ID, provideZoneChangeDetection } from "@angular/core";
import { NavigationEnd, ResolveEnd } from "@angular/router";
import { appRoutes } from "./app.routes";
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withI18nSupport, withIncrementalHydration } from "@angular/platform-browser";
import { HttpClient, provideHttpClient, withFetch } from "@angular/common/http";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import {
    LinkTag,
    metaImageLinkNormalize,
    PageMetadata,
    provideEnhancedRouting,
    providePageMetadata,
    providePreloadingStrategy,
    provideReuseAllStrategy,
    withOpenGraph,
    withTwitterCard,
} from "@upupa/common";
import { IMAGE_LOADER, ImageLoaderConfig, LocationStrategy } from "@angular/common";
import { provideApi } from "@upupa/data";
import { AnalyticsService } from "./ga.service";
import { AppService } from "./app.service";
import { AppConfig, ConfigService, provideConfig } from "./config.service";
import { firstValueFrom } from "rxjs";
function getFallbackSeo(appService: AppService, lang: string): Promise<{ lang: string; meta: PageMetadata }> {
    return appService
        .fetchJsonAsset(`/page?lang=${lang}&path=/fallback`)
        .catch((err) => {
            console.error(err);
            return { data: [] };
        })
        .then((res) => res.data?.[0] ?? { lang, meta: {} });
}

const enTags = [];
const arTags = [
    new LinkTag({ rel: "preconnect", href: "https://fonts.googleapis.com" }),
    new LinkTag({ rel: "preconnect", href: "https://fonts.gstatic.com", crossorigin: "anonymous" }),
    new LinkTag({ href: "https://fonts.googleapis.com/css2?family=Readex+Pro:wght@160..700&display=swap", rel: "stylesheet" }),
];

const imageLoading = (path: string, baseUrl: string, baseHref: string, assetsUrl: string): string => {
    const src = path || "";
    return src.startsWith("storage/") ? metaImageLinkNormalize(baseUrl, baseHref, src) : metaImageLinkNormalize(assetsUrl, baseHref, src);
};

export async function appConfig(): Promise<ApplicationConfig> {
    return {
        providers: [
            provideConfig(() => {
                const http = inject(HttpClient);
                return firstValueFrom(
                    http.get<AppConfig>(`/environments/environment.json`, { transferCache: true, headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } }),
                ).catch(() => ({}));
            }),
            provideApi(() => {
                const configService = inject(ConfigService);
                return configService.get("apiUrl");
            }),
            provideHttpClient(withFetch()),
            provideClientHydration(withI18nSupport(), withIncrementalHydration(), withHttpTransferCacheOptions({}), withEventReplay()),
            provideZoneChangeDetection({ eventCoalescing: true }),
            providePageMetadata(async () => {
                const locale = inject(LOCALE_ID);
                const locationStrategy = inject(LocationStrategy);
                const baseHref = locationStrategy.getBaseHref();
                const appService = inject(AppService);
                const configService = inject(ConfigService);
                const baseUrl = (await configService.get("baseUrl")) as string;
                const assetsUrl = (await configService.get("assetsUrl")) as string;
                const appMeta = await getFallbackSeo(appService, locale);

                const meta = { ...appMeta.meta };

                return {
                    canonicalBaseUrl: meta?.canonicalBaseUrl,

                    titleTemplate: (t, meta, fallback) => {
                        const siteName = fallback?.title || "";
                        const title = meta?.title || t || "";
                        if (title.length && siteName.length) return `${title} | ${siteName}`;
                        return title || siteName || "";
                    },
                    imageLoading: (path) => imageLoading(path, baseUrl, baseHref, assetsUrl),
                    fallback: {
                        image: `/assets/video-placeholder.webp`,
                        ...meta,
                        externalLinks: (locale === "ar" ? arTags : enTags).concat([
                            new LinkTag({ rel: "icon", href: `assets/android-chrome-512x512.png`, type: "image/png", sizes: "512x512" }),
                            new LinkTag({ rel: "icon", href: `assets/android-chrome-192x192.png`, type: "image/png", sizes: "192x192" }),
                            new LinkTag({ rel: "icon", href: `assets/favicon-32x32.png`, type: "image/png", sizes: "32x32" }),
                            new LinkTag({ rel: "icon", href: `assets/favicon-16x16.png`, type: "image/png", sizes: "16x16" }),
                            new LinkTag({ rel: "icon", href: `assets/favicon.ico`, type: "image/x-icon" }),
                            new LinkTag({ rel: "apple-touch-icon", href: `assets/favicon.png` }),
                            new LinkTag({ rel: "manifest", href: `site.webmanifest` }),
                            new LinkTag({ rel: "shortcut icon", href: `assets/favicon.ico` }),
                        ]),
                    } as PageMetadata,
                };
            }),
            withTwitterCard(async () => {
                const locationStrategy = inject(LocationStrategy);
                const baseHref = locationStrategy.getBaseHref();
                const configService = inject(ConfigService);
                const baseUrl = (await configService.get("baseUrl")) as string;
                const assetsUrl = (await configService.get("assetsUrl")) as string;
                return {
                    imageLoading: (path) => imageLoading(path, baseUrl, baseHref, assetsUrl),
                };
            }),
            withOpenGraph(async () => {
                const locationStrategy = inject(LocationStrategy);
                const baseHref = locationStrategy.getBaseHref();
                const configService = inject(ConfigService);

                const baseUrl = (await configService.get("baseUrl")) as string;
                const assetsUrl = (await configService.get("assetsUrl")) as string;

                return {
                    imageLoading: (path) => imageLoading(path, baseUrl, baseHref, assetsUrl),
                };
            }),

            {
                provide: IMAGE_LOADER,
                useFactory: (locationStrategy: LocationStrategy) => {
                    const baseHref = locationStrategy.getBaseHref();
                    const configService = inject(ConfigService);
                    return (config: ImageLoaderConfig) => {
                        const baseUrl = configService.getSync("baseUrl");
                        const assetsUrl = configService.getSync("assetsUrl") as string;

                        const src = config.src || "";
                        return src.startsWith("storage/") ? metaImageLinkNormalize(baseUrl, baseHref, src) : metaImageLinkNormalize(assetsUrl, baseHref, src);
                    };
                },
                deps: [LocationStrategy],
            },
            provideEnhancedRouting(appRoutes, {
                withDebugTracing: false,
                withViewTransitions: true,
                withComponentInputBinding: true,
                withEventHandler: (event, route) => {
                    //map data.$ to data

                    if (event instanceof ResolveEnd && route.data?.["$"]) {
                        route.data = { ...route.data, ...route.data["$"] };
                    }

                    //GA
                    if (event instanceof NavigationEnd) {
                        // const baseHref = inject(LocationStrategy).getBaseHref();
                        // const url = event.urlAfterRedirects || event.url;
                        // const robots = [`${baseHref}panel`];
                        // if (robots.some((path) => url.startsWith(path))) return;

                        inject(AnalyticsService).trackPageView(event.urlAfterRedirects);
                    }
                },
                resolveNavigationPromiseOnError: true,
                withInMemoryScrolling: {
                    anchorScrolling: "enabled",
                    scrollPositionRestoration: "enabled",
                },
            }),
            provideAnimationsAsync(),
            provideReuseAllStrategy(),
            providePreloadingStrategy(),
        ],
    };
}
